import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { cx } from '../lib/cx';

/**
 * Transient confirmation, shared by every screen.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 * Exactly one screen had it. `PlanBoard.tsx` carried a local `toast` state and
 * a `say()` helper, so committing a card to a sprint from Planning confirmed
 * itself — and the identical mutation from the request detail pane said
 * nothing at all. Everywhere else, the only feedback was an inline
 * `role="alert"` on failure: success was silent, which on a drag surface reads
 * as "did that work?".
 *
 * Spec v0.5 §5.4 lists "toast" among the components this product needs. This is
 * it, in one place, so the answer to "did that work" does not depend on which
 * screen you were standing on.
 *
 * ── Announced, not just drawn ───────────────────────────────────────────────
 * The live region is the point, not the box. `role="status"` for a
 * confirmation and `role="alert"` for a failure, so a screen-reader user hears
 * the outcome of a drag they cannot see. A visual-only toast tells the people
 * who least need telling.
 *
 * ── Why it is deliberately small ────────────────────────────────────────────
 * One toast at a time, replaced rather than stacked, and dismissed on a timer.
 * A queue is a real feature with real questions (ordering, pausing on hover,
 * how many is too many) and nothing here needs one: these are confirmations of
 * things the person just did, and the most recent is the one that matters.
 */

type Tone = 'info' | 'error';

interface ToastState {
  readonly text: string;
  readonly tone: Tone;
  /** Changes on every call, so repeating the same message re-announces it. */
  readonly nonce: number;
}

interface ToastApi {
  /** Confirm something. */
  readonly say: (text: string) => void;
  /** Report a failure. Announced assertively. */
  readonly warn: (text: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DISMISS_AFTER_MS = 4_000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);

  const show = useCallback((text: string, tone: Tone) => {
    const nonce = Date.now();
    setToast({ text, tone, nonce });
    setTimeout(() => {
      // Only clear if nothing newer has arrived, or a fast second action would
      // have its confirmation cut short by the first one's timer.
      setToast((current) => (current?.nonce === nonce ? null : current));
    }, DISMISS_AFTER_MS);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      say: (text: string) => show(text, 'info'),
      warn: (text: string) => show(text, 'error'),
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* The region exists whether or not anything is in it: a live region
          added to the DOM at the same moment as its content is not reliably
          announced. */}
      <div
        aria-live={toast?.tone === 'error' ? 'assertive' : 'polite'}
        role={toast?.tone === 'error' ? 'alert' : 'status'}
        className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex justify-center px-4"
      >
        {toast !== null && (
          <span
            key={toast.nonce}
            className={cx(
              'animate-enter rounded-lg px-3 py-2 fs-12.5 font-medium text-white shadow-lg',
              toast.tone === 'error' ? 'bg-danger' : 'bg-ink',
            )}
          >
            {toast.text}
          </span>
        )}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * `const { say, warn } = useToast()`.
 *
 * Throws outside the provider rather than returning a no-op: a confirmation
 * that silently does not appear is the bug this component exists to fix, and
 * it would be invisible in review.
 */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (api === null) {
    throw new Error('useToast must be used inside <ToastProvider>. It is mounted in AppShell.');
  }
  return api;
}
